import { useState } from 'react'
import { Download, Puzzle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useExtensionStatus, versionAtLeast } from './useExtensionStatus'

/** The first extension version that announces itself to the app. Anything older looks 'not installed'. */
export const MIN_EXTENSION_VERSION = '0.3.1'
const DISMISSED_KEY = 'tapwork:extension-prompt-dismissed'

/** Desktop Chrome, Edge, Brave, Arc — anything that can load the extension. Not phones or Safari/Firefox. */
function canInstallExtension(): boolean {
  const ua = navigator.userAgent
  return /Chrome\//.test(ua) && !/Mobile|Android|OPR\/|Firefox/.test(ua)
}

function dismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Asks anyone without the Tapwork extension to install it, every time they open the app.
 * "Not now" hides it for this tab only, so the next visit asks again; once the current
 * extension is installed it never appears. An out-of-date install is asked to update instead.
 */
export function ExtensionPrompt() {
  const [hidden, setHidden] = useState(dismissedThisSession)
  const [showSteps, setShowSteps] = useState(false)

  const { installed: version, latest, legacy } = useExtensionStatus()
  const outdated = legacy || (version !== null && !versionAtLeast(version, latest ?? MIN_EXTENSION_VERSION))
  if (hidden || !canInstallExtension() || (version !== null && !outdated)) return null

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1')
    } catch {
      /* hiding for this render is enough */
    }
    setHidden(true)
  }

  return (
    <div role="alert" aria-label="Tapwork extension" data-island-avoid className="border-b-2 border-brand bg-brand-soft px-3 py-3 shadow-sm md:px-5">
      <div className="mx-auto flex max-w-[var(--tt-page-max)] flex-wrap items-center gap-x-4 gap-y-2">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand text-text-inverse" aria-hidden="true">
          <Puzzle className="size-5" />
        </span>
        <p className="min-w-0 flex-1 text-sm text-text-primary">
          <span className="font-semibold">{outdated ? 'Update the Tapwork extension.' : `Get the Tapwork extension${latest ? ` (v${latest})` : ''}.`}</span>{' '}
          <span className="text-text-primary/80">
            {outdated
              ? `Latest is v${latest ?? MIN_EXTENSION_VERSION}. ${version ? `You have v${version}` : 'Yours is older than v0.3.1'} — please update.`
              : 'Turn any email into a task in one click, and see in Gmail which emails are already on Tapwork.'}
          </span>
        </p>
        <div className="flex shrink-0 items-center gap-1">
          <Button size="sm" className="h-9 gap-1.5 font-semibold shadow-sm" asChild>
            <a href="/downloads/tapwork-chrome-extension.zip" download="tapwork-chrome-extension.zip">
              <Download className="size-4" />
              {outdated ? 'Download update' : `Download extension${latest ? ` v${latest}` : ''}`}
            </a>
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-8" onClick={() => setShowSteps((open) => !open)} aria-expanded={showSteps}>
            How to install
          </Button>
          <Button type="button" variant="ghost" size="icon" className="size-8" onClick={dismiss} aria-label="Not now">
            <X className="size-4" />
          </Button>
        </div>
        {showSteps && (
          <ol className="w-full list-decimal space-y-0.5 pl-5 text-sm text-text-secondary">
            <li>Unzip the downloaded file.</li>
            <li>Open <span className="font-mono text-tiny">chrome://extensions</span> and turn on <strong>Developer mode</strong>.</li>
            <li>Click <strong>Load unpacked</strong> and choose the unzipped folder{outdated ? ' (replace the old one, then reload)' : ''}.</li>
            <li>Reload this page, then open Gmail.</li>
          </ol>
        )}
      </div>
    </div>
  )
}
